// Relation avec une personne, de son côté (conception, § 5) : palier montré sur la carte du contact, et
// « Prescripteur acquis » réglé d'un clic. La base fait monter les paliers d'après le journal.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Award } from "lucide-react";
import { useNotify, useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";

import { getSupabaseClient } from "../providers/supabase/supabase";

export type RelationStatus = "contacted" | "in_touch" | "specifier";

export const useContactRelations = () =>
  useQuery({
    queryKey: ["contact_relations"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("contact_relations")
        .select("contact_id, status");
      if (error) throw error;
      // Objet simple et non Map : le cache des requêtes est enregistré dans le navigateur, où un Map devient {}.
      return Object.fromEntries(
        (data ?? []).map((row) => [
          String(row.contact_id),
          row.status as RelationStatus,
        ]),
      ) as Record<string, RelationStatus>;
    },
  });

export const ContactRelation = ({
  contactId,
  canWrite,
}: {
  contactId: number;
  canWrite: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { data: relations } = useContactRelations();
  const status = relations?.[String(contactId)];
  const t = (key: string) =>
    translate(`resources.projects.contacts.relation.${key}`);
  const toggle = async () => {
    const { error } = await getSupabaseClient().rpc("set_specifier", {
      contact: contactId,
      on_off: status !== "specifier",
    });
    if (error) notify(error.message, { type: "error" });
    queryClient.invalidateQueries({ queryKey: ["contact_relations"] });
  };
  return (
    <span className="inline-flex items-center gap-2">
      {status && (
        <Badge variant={status === "specifier" ? "default" : "secondary"}>
          {t(status)}
        </Badge>
      )}
      {canWrite && (
        <button
          type="button"
          onClick={toggle}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          <Award className="size-3" />
          {status === "specifier" ? t("unset_specifier") : t("set_specifier")}
        </button>
      )}
    </span>
  );
};
